<template>
  <div
    class="position-relative mx-auto"
    :class="{
      'w-90': variant === 'card',
      'w-auto': variant === 'detail'
    }"
  >
    <div :class="['image-container', variant === 'detail' ? 'image-detail' : '']">
      <!-- Renderizar imagen principal -->
      <template v-if="images.length > 1">
        <div
          v-for="(image, index) in images"
          :key="index"
          v-show="currentImageIndex === index"
          class="image-wrapper"
        >
          <img :src="image" :alt="`Imagen ${index + 1}`" class="img-fluid" />
          <div v-if="watermarkActive && watermark" class="watermark">{{ watermark }}</div>
        </div>
      </template>
      <template v-else-if="images.length === 1">
        <div class="image-wrapper">
          <img :src="images[0]" alt="Imagen del producto" class="img-fluid" />
          <div v-if="watermarkActive && watermark" class="watermark">{{ watermark }}</div>
        </div>
      </template>
      <div class="position-absolute bottom-0 start-0" v-if="Object.keys(product).length > 0">
        <ProductIcons :product="product" />
      </div>

      <!-- Renderizar iconos dinámicos -->
      <template v-if="processedIcons.length > 0">
        <div
          v-for="(icon, index) in processedIcons"
          :key="index"
          :class="['position-absolute', icon.position]"
          :title="icon.tooltip"
        >
          <!-- Iconos como imágenes -->
          <img
            v-if="icon.type === 'image' && icon.src"
            :src="icon.src"
            :alt="icon.alt"
            class="img-fluid"
            :class="icon.class"
          />

          <!-- Iconos como badges -->
          <Badge
            v-else-if="icon.type === 'badge'"
            :tooltip="icon.tooltip"
            :bgClass="icon.bgClass"
            :defaultContent="icon.content"
          />

          <!-- Iconos como FontAwesome -->
          <FontAwesomeBadge
            v-else-if="icon.type === 'fontawesome'"
            :tooltip="icon.tooltip"
            :icon="icon.icon"
            :iconClass="icon.iconClass"
            :text="icon.text"
            :textClass="icon.textClass"
            :transform="icon.transform"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, toRefs } from 'vue'
import Badge from '@/components/shared/BadgeComponent.vue'
import FontAwesomeBadge from '@/components/shared/FontAwesomeBadge.vue'
import ProductIcons from '@/components/products/ProductIcons.vue'

const props = defineProps({
  product: {
    type: Object,
    required: false,
    default: () => ({})
  },
  images: {
    type: Array,
    required: true,
    default: () => []
  },
  watermark: {
    type: String,
    required: false,
    default: ''
  },
  watermarkActive: {
    type: Boolean,
    required: false,
    default: false
  },
  icons: {
    type: Array,
    required: true,
    default: () => []
  },
  autoCarousel: {
    type: Boolean,
    required: false,
    default: false
  },
  carouselInterval: {
    type: Number,
    required: false,
    default: 3000 // 3 segundos
  },
  variant: {
    type: String,
    required: false,
    default: 'card' // opciones: 'card' o 'detail'
  }
})

const { images, watermark, icons, autoCarousel, carouselInterval } = toRefs(props)

const currentImageIndex = ref(0)
let carouselTimer = null

// Posiciones disponibles en el sentido de las manecillas del reloj
const availablePositions = [
  'top-0 start-0',
  'top-0 start-50',
  'top-0 end-0',
  'top-50 end-0',
  'bottom-0 end-0',
  'bottom-0 start-50',
  'top-50 start-0'
]

// Procesar iconos dinámicos
const processedIcons = computed(() => {
  if (!icons.value || icons.value.length === 0) return []
  const usedPositions = new Set()
  const iconsWithPositions = []

  icons.value.forEach((icon) => {
    // Validar si el icono tiene `state` y si está activo
    if (!icon?.state) return
    let position = icon.position
    if (!position || usedPositions.has(position)) {
      // Asignar la siguiente posición disponible
      position = availablePositions.find((pos) => !usedPositions.has(pos))
    }
    if (position) {
      iconsWithPositions.push({ ...icon, position })
      usedPositions.add(position)
    }
  })

  return iconsWithPositions
})

// Carrusel automático
function startCarousel() {
  if (autoCarousel.value && images.value.length > 1) {
    carouselTimer = setInterval(() => {
      currentImageIndex.value = (currentImageIndex.value + 1) % images.value.length
    }, carouselInterval)
  }
}

function stopCarousel() {
  if (carouselTimer) {
    clearInterval(carouselTimer)
    carouselTimer = null
  }
}

onMounted(() => {
  startCarousel()
})

onUnmounted(() => {
  stopCarousel()
})
</script>

<style lang="scss">
.image-detail {
  max-height: 450px;
  min-height: 300px;
  background-color: white;
  border-radius: 10px;
}

.image-container {
  max-height: 350px;
  min-height: 200px;
  aspect-ratio: 2 / 2;
  width: 100%;
  overflow: hidden;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.image-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.image-wrapper img {
  width: auto;
  height: 100%;
  object-fit: contain;
}

.watermark {
  position: absolute;
  top: 95%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.8rem;
  color: #000000b3;
  animation: pulsewatermark 1.5s infinite;
}

@keyframes pulsewatermark {
  0% {
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.1);
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
}
</style>
